import * as yup from 'yup';
import { Artist } from 'types';
import { getActiveEventsPuzzlesParticipateInByCreator } from 'services/utility/puzzles';
import { Status } from 'shared/enums';

import { strings } from '../strings';
import { getArtistByProfileLink } from '../services';

import { artistDisplayNameValidator, MAX_CHARACTERS_LENGTH, spacesValidator } from './string';

export enum UpdateField {
  Name = 'name',
  City = 'city',
  RevenueShare = 'revenueShare',
  Country = 'country',
  Description = 'description',
  DisplayName = 'DisplayName',
  ProfileLink = 'profileLink',
  PortfolioLink = 'portfolioUrl',
  Status = 'status',
  Reason = 'reason',
  RejectMessage = 'rejectMessage',
  Suspended = 'suspended',
}

type EditAccountsUpdateSchemaOptions = {
  removeDescriptionLengthValidators?: boolean;
  removeArtistDisplayNameLengthValidators?: boolean;
};

export const updateSchema = (
  artistId: string,
  artistData?: Artist,
  options?: EditAccountsUpdateSchemaOptions,
) => {
  const shape = {
    [UpdateField.Name]: yup
      .string()
      .trim()
      .test(
        'duplicate-specials',
        'Please avoid using multiple spaces, dashes and apostrophes',
        (value) => spacesValidator(value ?? ''),
      )
      .max(
        MAX_CHARACTERS_LENGTH.FIRST_NAME_LAST_NAME_MAX_LENGTH,
        `Name can't be longer than ${MAX_CHARACTERS_LENGTH.FIRST_NAME_LAST_NAME_MAX_LENGTH} characters`,
      )
      .required(strings.createAccNameRequired),
    [UpdateField.DisplayName]: artistDisplayNameValidator(artistData ?? undefined, {
      removeArtistDisplayNameLengthValidators: options?.removeArtistDisplayNameLengthValidators,
    }),
    [UpdateField.RevenueShare]: yup
      .number()
      .positive()
      .transform((v) => (isNaN(v) ? undefined : v))
      .required(strings.revenueRequired)
      .max(100, 'Revenue can not be bigger than 100')
      .min(0, 'Revenue share can not be smaller than 0'),
    [UpdateField.ProfileLink]: yup
      .string()
      .required('Profile Link is missing')
      .matches(/^[a-zA-Z0-9]+$/, 'Profile Link must contain only letters and numbers!')
      .min(5, 'Profile Link must contain at least 5 characters.')
      .max(30, 'Profile Link can not be longer than 30 characters.')
      // TODO We need to add debounce to avoid run it to frequently. we need to debounce whole form validation
      .test('is-unique', 'Profile Link must be unique', async (value, context) => {
        const userWithProfileLink = await getArtistByProfileLink(value);

        if (userWithProfileLink && userWithProfileLink.id !== artistId) {
          return context.createError({
            message: `Profile Link must be unique. Exists user with the same id: ${userWithProfileLink.id}`,
          });
        }

        return true;
      }),
    [UpdateField.Status]: yup
      .object({
        label: yup.string(),
        value: yup.string().required(strings.collectionIsRequired),
      })
      .test(
        'hasContentInActiveEvent',
        'Creator has content in active event',
        async (option, context) => {
          const optionValue = option.value;
          const checkStatuses: string[] = [
            Status.Deleted,
            Status.Disabled,
            Status.Pending,
            Status.Rejected,
          ];

          if (checkStatuses.includes(optionValue)) {
            const activeEventsCreatorHasPuzzlesIn =
              await getActiveEventsPuzzlesParticipateInByCreator(artistId);

            if (activeEventsCreatorHasPuzzlesIn.length > 0) {
              const activeEventsString = activeEventsCreatorHasPuzzlesIn
                .map((event) => event.event.id)
                .join(', ');

              return context.createError({
                message: `Creator has puzzles in active events: ${activeEventsString}`,
              });
            }
          }

          return true;
        },
      ),
    [UpdateField.PortfolioLink]: yup.string().trim(),
    [UpdateField.Description]: yup.string().trim(),
    // Couldn't make it depend on status field because value isn't stored in db and would pose problems
    // when page is freshly loaded with "disabled" as starting status. This would require admin to type
    // in reason each time any another field is changed which doesn't make sense.
    // So this check is added during submit phase.
    [UpdateField.Reason]: yup.string(),
    [UpdateField.Suspended]: yup.boolean(),

    [UpdateField.RejectMessage]: yup.string().when(UpdateField.Status, {
      is: (status: { label: string; value: string }) => status.value === Status.Rejected,
      then: () => yup.string().required('Rejection message is required.'),
      otherwise: () => yup.string(),
    }),
  };
  if (!options || !options.removeDescriptionLengthValidators) {
    shape[UpdateField.Description] = shape[UpdateField.Description].max(
      2000,
      'Description cannot be longer than 2000 characters.',
    );
  }

  return yup.object().shape(shape);
};
